import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSetAtom } from 'jotai';
import { DiscoverQs } from '@src/types/Discover';
import { QsNames } from '@src/types/Browsing';
import { pathLocationToString, setQsParam } from '@src/utils/browsing';
import { useLocationQS } from '@src/utils/useLocationQS';
import { XclusionUtility } from '@src/fragments/Filters/managers/XclusionUtility';
import {
  filterCollectionXclusionState,
  filterCreatorCountriesXclusionState,
  filterCreatorXclusionState,
  filterLanguageXclusionState,
  FilterState,
  filterOwnContentXclusionState,
  filterProductExactXclusionState,
  filterProductSimilarXclusionState,
} from '@src/pages/Discover/components/Atoms/Atoms';

export const filtersParamsKeys: (keyof DiscoverQs)[] = [
  QsNames.discoverExactMode,
  QsNames.discoverSearchText,
  QsNames.discoverSearchHashtags,
  QsNames.discoverSearchLabels,
  QsNames.discoverFiltersSource,
  QsNames.discoverFiltersMedia,
  QsNames.discoverFiltersAudio,
  QsNames.discoverFiltersAudioLang,
  QsNames.discoverFiltersMediaRRStatuses,
  QsNames.discoverFiltersCreator,
  QsNames.discoverFiltersDate,
  QsNames.discoverFiltersLang,
  QsNames.discoverFiltersProductExact,
  QsNames.discoverFiltersProductSimilar,
  QsNames.discoverFiltersCollection,
  QsNames.discoverFiltersOwnContent,
  QsNames.discoverFiltersProductCatalogCountries,
  QsNames.discoverFiltersCreatorCountries,
];

export const defaultFiltersParams: DiscoverQs = {
  exact: [],
  caption: [],
  hashtag: [],
  label: [],
  source: [],
  media: [],
  audio: [],
  audioLang: [],
  rrStatus: [],
  creator: [],
  date: [],
  lang: [],
  productExact: [],
  productSimilar: [],
  collection: [],
  ownContent: [],
  productCatalogCountries: [],
  creatorCountries: [],
};

type FiltersUpdater = DiscoverQs | ((filters: DiscoverQs) => DiscoverQs);

interface UseFiltersParamsOptions {
  qs?: DiscoverQs;
}

// Hook to synchronize filters state with URL search params
// Note: Not sure about the naming as it's not only about the filters section (e.g. exact mode is also included)
export function useFiltersParams(options: UseFiltersParamsOptions = {}) {
  const location = useLocation();
  const navigate = useNavigate();
  const qs = useLocationQS(filtersParamsKeys);
  const filtersParams = options.qs || qs;

  const setFilterState = useSetAtom(FilterState);
  const setFilterCreatorXclusion = useSetAtom(filterCreatorXclusionState);
  const setFilterCollectionXclusion = useSetAtom(filterCollectionXclusionState);
  const setFilterLanguageXclusion = useSetAtom(filterLanguageXclusionState);
  const setFilterProductExactXclusion = useSetAtom(filterProductExactXclusionState);
  const setFilterProductSimilarXclusion = useSetAtom(filterProductSimilarXclusionState);
  const setFilterOwnContentXclusion = useSetAtom(filterOwnContentXclusionState);
  const setFilterCreatorCountriesXclusion = useSetAtom(filterCreatorCountriesXclusionState);

  useEffect(() => {
    setFilterState(filtersParams);
    setFilterCreatorXclusion(XclusionUtility.prevailing(filtersParams.creator));
    setFilterCollectionXclusion(XclusionUtility.prevailing(filtersParams.collection));
    setFilterLanguageXclusion(XclusionUtility.prevailing(filtersParams.lang));
    setFilterProductExactXclusion(XclusionUtility.prevailing(filtersParams.productExact));
    setFilterProductSimilarXclusion(XclusionUtility.prevailing(filtersParams.productSimilar));
    setFilterOwnContentXclusion(XclusionUtility.prevailing(filtersParams.ownContent));
    setFilterCreatorCountriesXclusion(XclusionUtility.prevailing(filtersParams.creatorCountries));
  }, [
    filtersParams,
    setFilterState,
    setFilterCreatorXclusion,
    setFilterCollectionXclusion,
    setFilterLanguageXclusion,
    setFilterProductExactXclusion,
    setFilterProductSimilarXclusion,
    setFilterOwnContentXclusion,
    setFilterCreatorCountriesXclusion,
  ]);

  const setFiltersParams = useCallback((updater: FiltersUpdater) => {
    const nextFiltersParams = typeof updater === 'function'
      ? updater(filtersParams)
      : updater;

    const changes = filtersParamsKeys
      .filter((field) => !areValuesEqual(filtersParams[field], nextFiltersParams[field]))
      .map((field) => ({
        field,
        val: nextFiltersParams[field],
      }));

    if (!changes.length) {
      return;
    }

    const nextLocation = pathLocationToString(setQsParam(location, changes));
    navigate(nextLocation);
  }, [filtersParams, location, navigate]);

  const isEmpty = useMemo(() => areFiltersEmpty(filtersParams), [filtersParams]);

  return {
    filters: filtersParams,
    setFilters: setFiltersParams,
    isEmpty,
  };
}

function areFiltersEmpty(filtersParams: DiscoverQs): boolean {
  return filtersParamsKeys.every((field) => filtersParams[field].length === 0);
}

function areValuesEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  return a.every((value, index) => value === b[index]);
}
